import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Stack,
    Button,
    ButtonGroup,
    Card,
    Divider,
    FormControl,
    FormLabel,
    Input,
    IconButton,
    Textarea
} from '@mui/joy';
import CodeMirror from '@uiw/react-codemirror';
import { yaml } from '@codemirror/lang-yaml';
import jsyaml from 'js-yaml';
import EditIcon from '@mui/icons-material/Edit';
import CodeIcon from '@mui/icons-material/Code';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';

// --- Utilities ---

const updateValueAtPath = (obj, path, value) => {
    if (path.length === 0) return value;
    const [head, ...tail] = path;
    const isArray = Array.isArray(obj);
    const newObj = isArray ? [...obj] : { ...obj };
    newObj[head] = updateValueAtPath(obj[head], tail, value);
    return newObj;
};

const removeValueAtPath = (obj, path) => {
    if (path.length === 1) {
        const [key] = path;
        const isArray = Array.isArray(obj);
        if (isArray) {
            const newArr = [...obj];
            newArr.splice(key, 1);
            return newArr;
        } else {
            const { [key]: _, ...rest } = obj;
            return rest;
        }
    }
    const [head, ...tail] = path;
    const isArray = Array.isArray(obj);
    const newObj = isArray ? [...obj] : { ...obj };
    newObj[head] = removeValueAtPath(obj[head], tail);
    return newObj;
};

const renameKeyAtPath = (obj, path, oldKey, newKey) => {
    if (path.length === 0) {
        const { [oldKey]: val, ...rest } = obj;
        return { ...rest, [newKey]: val };
    }
    const [head, ...tail] = path;
    const isArray = Array.isArray(obj);
    const newObj = isArray ? [...obj] : { ...obj };
    newObj[head] = renameKeyAtPath(obj[head], tail, oldKey, newKey);
    return newObj;
};

// --- Sub-components ---

const ModeToggle = ({ mode, onModeChange, hasError }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <ButtonGroup
            variant="soft"
            color="primary"
            size="sm"
            sx={{ borderRadius: 'lg', p: 0.5, bgcolor: 'background.level1' }}
        >
            <Button
                startDecorator={<AutoFixHighIcon />}
                onClick={() => onModeChange('visual')}
                variant={mode === 'visual' ? 'solid' : 'plain'}
                sx={{ borderRadius: 'md' }}
            >
                Visual Editor
            </Button>
            <Button
                startDecorator={<CodeIcon />}
                onClick={() => onModeChange('code')}
                variant={mode === 'code' ? 'solid' : 'plain'}
                sx={{ borderRadius: 'md' }}
            >
                Code Editor
            </Button>
        </ButtonGroup>

        {hasError && mode === 'code' && (
            <Typography level="body-xs" color="danger" sx={{ fontWeight: 'bold' }}>
                ⚠️ {hasError}
            </Typography>
        )}
    </Box>
);

const CodeEditorView = ({ value, onChange, onCodeChange }) => (
    <CodeMirror
        value={value || ''}
        height="400px"
        theme="dark"
        extensions={[yaml()]}
        onChange={onCodeChange}
        style={{ fontSize: '14px' }}
    />
);

const EmptyVisualState = ({ onAddField }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, opacity: 0.5 }}>
        <EditIcon sx={{ fontSize: 48, mb: 1 }} />
        <Typography>No fields defined in job configuration</Typography>
        <Button
            size="sm"
            variant="plain"
            startDecorator={<AddIcon />}
            onClick={() => onAddField([])}
            sx={{ mt: 1 }}
        >
            Add First Field
        </Button>
    </Box>
);

const FieldRow = ({
    path,
    fieldKey,
    value,
    onRename,
    onUpdate,
    onDelete,
    onAddField,
    level = 0
}) => {
    const isObject = value !== null && typeof value === 'object' && !Array.isArray(value);
    const isArray = Array.isArray(value);
    const isPrimitive = !isObject && !isArray;

    return (
        <Stack spacing={1} sx={{ ml: level > 0 ? 3 : 0, borderLeft: level > 0 ? '1px solid' : 'none', borderColor: 'divider', pl: level > 0 ? 2 : 0 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl sx={{ width: '150px' }}>
                    <Input
                        size="sm"
                        value={fieldKey}
                        variant="plain"
                        onBlur={(e) => onRename(path, fieldKey, e.target.value)}
                        sx={{ fontWeight: 'bold', bgcolor: 'transparent', '&:hover': { bgcolor: 'background.level1' } }}
                        placeholder="key"
                        disabled={typeof fieldKey === 'number'} // Don't rename array indexes
                    />
                </FormControl>

                {isPrimitive ? (
                    <FormControl sx={{ flex: 1 }}>
                        <Textarea
                            size="sm"
                            minRows={1}
                            value={value || ''}
                            onChange={(e) => onUpdate([...path, fieldKey], e.target.value)}
                            sx={{ fontFamily: 'monospace' }}
                        />
                    </FormControl>
                ) : (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                        <Typography level="body-xs" sx={{ fontStyle: 'italic', color: 'neutral.500' }}>
                            {isArray ? 'Array' : 'Object'}
                        </Typography>
                    </Box>
                )}

                <Stack direction="row" spacing={0.5}>
                    {(isObject || isArray) && (
                        <IconButton
                            size="sm"
                            variant="plain"
                            onClick={() => onAddField([...path, fieldKey])}
                        >
                            <AddIcon />
                        </IconButton>
                    )}
                    <IconButton
                        size="sm"
                        color="danger"
                        variant="plain"
                        onClick={() => onDelete([...path, fieldKey])}
                    >
                        <DeleteOutlineIcon />
                    </IconButton>
                </Stack>
            </Box>

            {!isPrimitive && (
                <Stack spacing={1}>
                    {isArray ? (
                        value.map((item, index) => (
                            <FieldRow
                                key={index}
                                path={[...path, fieldKey]}
                                fieldKey={index}
                                value={item}
                                onRename={onRename}
                                onUpdate={onUpdate}
                                onDelete={onDelete}
                                onAddField={onAddField}
                                level={level + 1}
                            />
                        ))
                    ) : (
                        Object.entries(value).map(([childKey, childValue]) => (
                            <FieldRow
                                key={childKey}
                                path={[...path, fieldKey]}
                                fieldKey={childKey}
                                value={childValue}
                                onRename={onRename}
                                onUpdate={onUpdate}
                                onDelete={onDelete}
                                onAddField={onAddField}
                                level={level + 1}
                            />
                        ))
                    )}
                    {Object.keys(value).length === 0 && (
                        <Typography level="body-xs" sx={{ ml: 3, opacity: 0.5 }}>Empty</Typography>
                    )}
                </Stack>
            )}
        </Stack>
    );
};

const VisualEditorView = ({ data, onUpdateField, onRemoveField, onAddField, onRenameKey }) => {
    if (!data || Object.keys(data).length === 0) {
        return <EmptyVisualState onAddField={onAddField} />;
    }

    return (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, maxHeight: '600px', overflowY: 'auto' }}>
            <Stack spacing={2}>
                {Object.entries(data).map(([key, val]) => (
                    <FieldRow
                        key={key}
                        path={[]}
                        fieldKey={key}
                        value={val}
                        onRename={onRenameKey}
                        onUpdate={onUpdateField}
                        onDelete={onRemoveField}
                        onAddField={onAddField}
                    />
                ))}
            </Stack>
            <Divider />
            <Button
                size="sm"
                variant="dashed"
                color="neutral"
                startDecorator={<AddIcon />}
                onClick={() => onAddField([])}
                sx={{ width: '100%', py: 1.5 }}
            >
                Add Field
            </Button>
        </Box>
    );
};

// --- Main Component ---

/**
 * JobConfigEditor Component
 * Provides two modes for editing YAML configuration:
 * Option A: Structured/Visual editor
 * Option B: Code editor with YAML syntax highlighting
 */
const JobConfigEditor = ({ value, onChange }) => {
    const [mode, setMode] = useState('visual'); // 'visual' or 'code'
    const [yamlError, setYamlError] = useState(null);
    const [visualData, setVisualData] = useState({});

    // Initialize/Sync visual data from YAML string
    useEffect(() => {
        try {
            if (value) {
                const parsed = jsyaml.load(value);
                if (parsed && typeof parsed === 'object') {
                    setVisualData(parsed);
                    setYamlError(null);
                }
            } else {
                setVisualData({});
            }
        } catch (e) {
            console.warn('Failed to parse YAML for visual editor:', e);
            setYamlError('Invalid YAML structure for visual editor. Use code mode to fix.');
        }
    }, [value]);

    const handleModeChange = (newMode) => {
        if (newMode === 'visual' && yamlError) return;
        setMode(newMode);
    };

    const handleCodeChange = (val) => {
        onChange(val);
        try {
            jsyaml.load(val);
            setYamlError(null);
        } catch (e) {
            setYamlError(e.message);
        }
    };

    const updateValue = (newData) => {
        setVisualData(newData);
        try {
            onChange(jsyaml.dump(newData, { noRefs: true, lineWidth: -1 }));
        } catch (e) {
            console.error('Failed to stringify YAML:', e);
        }
    };

    const handleUpdateField = (path, val) => {
        const newData = updateValueAtPath(visualData, path, val);
        updateValue(newData);
    };

    const handleRemoveField = (path) => {
        const newData = removeValueAtPath(visualData, path);
        updateValue(newData);
    };

    const handleAddField = (path) => {
        let parent = visualData;
        path.forEach(p => { parent = parent[p]; });

        const isArray = Array.isArray(parent);
        const newKey = isArray ? parent.length : `new_field_${Object.keys(parent).length + 1}`;

        const newData = updateValueAtPath(visualData, [...path, newKey], '');
        updateValue(newData);
    };

    const handleKeyRename = (path, oldKey, newKey) => {
        if (!newKey || oldKey === newKey) return;

        let parent = visualData;
        path.forEach(p => { parent = parent[p]; });

        if (parent[newKey] !== undefined) return;

        const newData = renameKeyAtPath(visualData, path, oldKey, newKey);
        updateValue(newData);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: '400px' }}>
            <ModeToggle
                mode={mode}
                onModeChange={handleModeChange}
                hasError={yamlError}
            />

            <Card
                variant="outlined"
                sx={{
                    flex: 1,
                    p: 0,
                    overflow: 'hidden',
                    bgcolor: mode === 'code' ? '#282c34' : 'background.surface'
                }}
            >
                {mode === 'code' ? (
                    <CodeEditorView
                        value={value}
                        onCodeChange={handleCodeChange}
                    />
                ) : (
                    <VisualEditorView
                        data={visualData}
                        onUpdateField={handleUpdateField}
                        onRemoveField={handleRemoveField}
                        onAddField={handleAddField}
                        onRenameKey={handleKeyRename}
                    />
                )}
            </Card>

            <Typography level="body-xs" variant="soft" color="neutral" sx={{ p: 1, borderRadius: 'sm' }}>
                <strong>Pro-tip:</strong> Use Code Editor for complex nesting or bulk pasting. Visual Editor is great for quick updates.
            </Typography>
        </Box>
    );
};

export default JobConfigEditor;

